import { Source } from 'react-map-gl';
import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { InlineNotification, SkeletonPlaceholder } from '@carbon/react';
import { EmptyState } from 'components/empty-state';
import { Map as MapComponent } from 'components/map';
import { getMapDefaultConfig } from 'components/map/atoms';
import { mapColors } from 'components/map/map-colors';
import { Layer, LayerBand, LAYER_ORDER } from 'components/map/ordered-layer';
import NoMapSvg from 'images/srp-illustration-no-map.svg';
import type { Feature, Geometry, GeoJsonProperties } from 'geojson';
import {
  IntersectingUnit,
  WellIntersectionAnalysisResponse,
  WellboreMapData,
} from 'types/api-responses';
import { formatMutationResource } from 'utils/misc';
import {
  isProducingVisibleAtom,
  isUnitsVisibleAtom,
  sidetrackVisibilityAtom,
  wellMapMutationAtom,
} from './atoms';
import type { TraceColors } from 'components/wells-color-by';
import { WellMapLayer } from 'components/well-map-layer';
import { useWellMapRequestOnce } from './well-map-request';
import { WellScopedLayers, WellboreSummary } from './well-scoped-layers';
import { useEffect } from 'react';
import style from './well-page-content.module.scss';

const WELLS_QUERY_HREF = '/queries/wells';

const mapConfigAtom = atomWithStorage(
  'insights-wells-show-map-config',
  getMapDefaultConfig({
    wells: {
      checked: true,
      options: {
        surfaceHole: true,
        bottomHole: true,
        stick: true,
        survey: true,
        vertical: true,
      },
    },
  })
);

/**
 * The well's map: its wellbores, and the units it intersects.
 *
 * Its own module rather than part of well-page-content, because three surfaces host the
 * same map and importing it from the page content would pull every other section along.
 * @param {object} props The component's props.
 * @param {string} props.uwi The well to map.
 * @param {string} [props.versionId] The well version to map, or the current one.
 * @param {WellIntersectionAnalysisResponse} [props.intersectionData] The intersecting
 * units, whose polygons are drawn under the wellbores.
 * @param {Set<string>} props.selectedUnitIds Which of those units to draw.
 * @param {number} [props.workspaceId] The Well Workspace in view, when there is one.
 * @return {JSX.Element} The map, or an empty state when the well cannot be placed.
 */
export const WellMap = ({
  uwi,
  versionId,
  intersectionData,
  selectedUnitIds,
  workspaceId,
  traceColors,
  refreshToken,
}: {
  uwi: string;
  versionId?: string;
  intersectionData?: WellIntersectionAnalysisResponse;
  selectedUnitIds: Set<string>;
  /**
   * The Well Workspace this map is being read inside (INS-12130). A non-strict
   * directive on the wells query: it does not change WHICH wells come back, only how
   * each row resolves — promoted resolves as-of `promoted_at`, draft overlays the
   * workspace's edited fields on the live record. Omitted on the Well page, which is
   * outside any workspace and reads live.
   */
  workspaceId?: number;
  /**
   * What each kind of trace should draw in, from the rail's colour-by rules. A kind is
   * absent when no rule covers it, which leaves the map's own default — a colour with no
   * rule behind it would be a meaning the map invented. Keyed by kind rather than a
   * single colour so a Wellbore Geometry rule can tell a producing bore from a sidetrack.
   */
  traceColors?: TraceColors;
  /**
   * Changes when the well is edited while the map is mounted (a wellbore added, rebuilt,
   * removed or re-pointed in a workspace). The wells query is a one-shot POST into this
   * card's own store — not a React Query read — so nothing invalidates it; a new token
   * makes the otherwise identical request go out again.
   */
  refreshToken?: number;
}) => {
  const [wellsData, setWellsData] = useAtom(wellMapMutationAtom);
  const { loading, data, error } = formatMutationResource(wellsData);
  const [isProducingVisible] = useAtom(isProducingVisibleAtom);
  const [sidetrackVisibility] = useAtom(sidetrackVisibilityAtom);
  const [isUnitsVisible] = useAtom(isUnitsVisibleAtom);

  // Fetch wells data with wellbores. Deduplicated per caller and per mount, not
  // globally: this card lives in its own ProvideRootResource store, so its fetch cannot
  // stand in for the Explorer's — and the store is new on every visit, so neither can a
  // fetch from the last visit.
  const wellMapRequest = useWellMapRequestOnce();
  useEffect(() => {
    if (!uwi) return;
    wellMapRequest.send(
      setWellsData,
      {
        url: WELLS_QUERY_HREF,
        type: 'POST',
        data: {
          uwi: uwi,
          includeDeviationSurvey2d: true,
          ...(versionId && versionId !== 'current'
            ? { versionTimestamp: versionId }
            : {}),
          // The controller underscores incoming keys, so this arrives as workspace_id.
          ...(workspaceId ? { workspaceId } : {}),
        },
      },
      refreshToken
    );
  }, [uwi, versionId, workspaceId, refreshToken, setWellsData, wellMapRequest]);

  // A failed query must not stay deduplicated, or this well would keep showing the
  // error without ever retrying.
  useEffect(() => {
    if (error?.type) wellMapRequest.forget();
  }, [error, wellMapRequest]);

  // Show loading state
  if (loading) {
    return <SkeletonPlaceholder className={style.mapLoad} />;
  }

  // Show error state
  if (!loading && error?.type) {
    return (
      <InlineNotification
        kind="error"
        title="Error"
        subtitle="Failed to load well map data"
        lowContrast
        hideCloseButton
      />
    );
  }

  // Get wellbores from API response
  const wellbores: WellboreMapData[] = data?.results?.[0]?.wellBores ?? [];

  // Get the producing wellbore for initial map center
  const producingWellbore = wellbores.find((wb) => wb.type === 'producing');

  // A survey-less estimated vertical has no producing bore, so fall back to the well-level
  // surface hole to center the map (mirrors the Subsurface Explorer gate).
  const surfaceCoordinates =
    producingWellbore?.surfaceHole?.coordinates ??
    data?.results?.[0]?.surfaceHole?.coordinates;

  if (!surfaceCoordinates) {
    return (
      <EmptyState
        icon={NoMapSvg}
        iconClassName={style.emptyMapIcon}
        headerText="No Data Available"
        helperText="Currently Insights doesn't have enough data to map this well."
      />
    );
  }

  const [longitude, latitude] = surfaceCoordinates;

  const hasIntersectingUnits =
    intersectionData && intersectionData.intersectingUnits.length > 0;

  // Get selected units that should be displayed on the map
  const selectedUnits = intersectionData?.intersectingUnits.filter((unit) =>
    selectedUnitIds.has(unit.name)
  );

  // Separate wellbores by type
  const producingWellbores = wellbores.filter((wb) => wb.type === 'producing');
  const sidetrackWellbores = wellbores.filter((wb) => wb.type === 'sidetrack');

  // Create wellbore summary for scoped layers
  const wellboreSummary: WellboreSummary = {
    producing: producingWellbores.length,
    sidetracks: sidetrackWellbores.map((wb) => wb.uwi14),
  };

  return (
    <MapComponent
      latitude={latitude}
      longitude={longitude}
      showControls
      showMapFilters
      className={style.wellsMap}
      mapConfigAtom={mapConfigAtom}
      scopedLayers={
        <WellScopedLayers
          hasIntersectingUnits={!!hasIntersectingUnits}
          wellboreSummary={wellboreSummary}
        />
      }
    >
      <>
        {/* Unit polygons, banded under the wellbores. A layer mounts on top of the
            stack and the units usually arrive after the wells, so an unbanded unit
            would draw over the subject. */}
        <LayerBand band={LAYER_ORDER.developmentAreas}>
          {isUnitsVisible &&
            selectedUnits?.map((unit: IntersectingUnit) => {
              if (!unit.geojson) return null;
              /* Blue marks the unit this well belongs to. A well authored in a workspace
                 has no assignment until one is saved, so the backend's recommendation
                 stands in — the same order Unit Details seeds its radio with, so the map
                 and the pre-selected row agree. `bestFit` is absent on the canonical
                 payload, which leaves existing wells reading exactly as before. */
              const isPrimary = unit.assignedToUnit || unit.bestFit;
              return (
                <Source
                  key={`unit-source-${unit.id}`}
                  type="geojson"
                  data={unit.geojson as Feature<Geometry, GeoJsonProperties>}
                >
                  {/* Fill layer for the unit polygon */}
                  <Layer
                    id={`development-area-fill-${unit.name}`}
                    type="fill"
                    paint={{
                      'fill-color': isPrimary
                        ? mapColors.assignedUnitFill
                        : mapColors.nonAssignedUnitFill,
                      'fill-opacity': isPrimary ? 0.6 : 0.4,
                    }}
                  />
                  {/* Outline layer for visible boundary */}
                  <Layer
                    id={`development-area-outline-${unit.name}`}
                    type="line"
                    paint={{
                      'line-color': isPrimary
                        ? mapColors.assignedUnitBorder
                        : mapColors.nonAssignedUnitBorder,
                      'line-width': 2,
                      'line-opacity': 1,
                    }}
                  />
                </Source>
              );
            })}
        </LayerBand>

        <LayerBand band={LAYER_ORDER.wells}>
          {/* Render producing wellbores */}
          {isProducingVisible &&
            producingWellbores.map((wellbore) => (
              <WellMapLayer
                key={`producing-${wellbore.uwi14}`}
                id={`producing-${wellbore.uwi14}`}
                wellbore={wellbore}
                mapConfigAtom={mapConfigAtom}
                colorOverride={traceColors?.completion ?? '#161616'}
              />
            ))}

          {/* Render sidetrack wellbores - each individually controlled */}
          {sidetrackWellbores
            .filter((wellbore) => sidetrackVisibility[wellbore.uwi14])
            .map((wellbore) => (
              <WellMapLayer
                key={`sidetrack-${wellbore.uwi14}`}
                id={`sidetrack-${wellbore.uwi14}`}
                wellbore={wellbore}
                mapConfigAtom={mapConfigAtom}
                // A rule on the well's own fields matches every bore, so this lands the
                // same colour as the producing trace; a Wellbore Geometry rule resolves
                // its own. No colour means no rule, so the sidetrack keeps its red.
                colorOverride={traceColors?.sidetrack}
              />
            ))}
        </LayerBand>
      </>
    </MapComponent>
  );
};
